/**
 * Symbol Mapping Service - Simplified Version
 * Provides simple runtime mapping for WebSocket responses only
 * Does NOT cache conversions - uses deterministic functions instead
 */

import {
  positionToStreamerSymbol,
  streamerToPositionSymbol,
} from "@/utils/option-symbol-utils";

export class SymbolMappingService {
  // Runtime map for WebSocket responses only
  // Maps streamer symbols to position symbols based on actual data received
  private wsResponseMap = new Map<string, string>();

  constructor() {
    // No localStorage - keep it simple and stateless
  }

  /**
   * Store a WebSocket response mapping
   * Only used when we receive actual data from the WebSocket
   */
  addWebSocketMapping(streamerSymbol: string, positionSymbol: string): void {
    // Only store if both symbols are valid
    if (
      streamerSymbol &&
      positionSymbol &&
      streamerSymbol.startsWith(".") &&
      positionSymbol.includes(" ")
    ) {
      this.wsResponseMap.set(streamerSymbol, positionSymbol);
    }
  }

  /**
   * Get streamer symbol for a position symbol
   * Always uses the deterministic conversion function
   */
  getStreamerSymbol(positionSymbol: string): string | null {
    // Always use the conversion function - it's deterministic
    const converted = positionToStreamerSymbol(positionSymbol);

    // Validate the result
    if (
      converted &&
      converted.startsWith(".") &&
      converted !== positionSymbol
    ) {
      return converted;
    }

    // If it didn't convert (e.g., for stocks), return null
    return null;
  }

  mapPositionToStreamer(positionSymbol: string): string | null {
    return this.getStreamerSymbol(positionSymbol);
  }

  /**
   * Get position symbol for a streamer symbol
   */
  getPositionSymbol(streamerSymbol: string): string | null {
    // 1. Check WebSocket response map first
    const wsMapping = this.wsResponseMap.get(streamerSymbol);
    if (wsMapping) return wsMapping;

    // 2. Use conversion function
    const converted = streamerToPositionSymbol(streamerSymbol);
    if (converted && converted !== streamerSymbol && converted.includes(" ")) {
      return converted;
    }

    return null;
  }

  /**
   * Check if we can convert a position symbol
   */
  hasMapping(positionSymbol: string): boolean {
    // We can always convert if the conversion function works
    const converted = this.getStreamerSymbol(positionSymbol);
    return converted !== null;
  }

  /**
   * Get WebSocket response mappings (for debugging)
   */
  getAllMappings(): {
    position: string;
    streamer: string;
    source: string;
  }[] {
    const mappings: {
      position: string;
      streamer: string;
      source: string;
    }[] = [];

    // Only show WebSocket response mappings
    this.wsResponseMap.forEach((position, streamer) => {
      mappings.push({
        position,
        streamer,
        source: "websocket",
      });
    });

    return mappings;
  }

  /**
   * Clear WebSocket mappings
   */
  clear(): void {
    this.wsResponseMap.clear();
  }

  /**
   * No invalid mappings to clear - conversions are always valid
   */
  clearInvalidMappings(): number {
    // This method is kept for compatibility but does nothing
    // since we no longer cache conversions
    return 0;
  }

  /**
   * Get statistics about the mappings
   */
  getStats(): {
    totalMappings: number;
    verifiedMappings: number;
    cacheKeys: number;
  } {
    return {
      totalMappings: this.wsResponseMap.size,
      verifiedMappings: this.wsResponseMap.size, // All WS mappings are verified
      cacheKeys: 0, // No cache keys in simplified version
    };
  }
}

// Singleton instance
export const symbolMappingService = new SymbolMappingService();
